import React, { useState, useEffect, useMemo, useCallback } from 'react';
import authService from '../../services/authService';
import { adminService } from '../../services/adminService';
import { useSnackbar } from 'notistack';
import {
  Box,
  Card,
  CardContent,
  Collapse,
  Divider,
  Grid,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  TextField,
  ListSubheader,
  InputAdornment,
  Tooltip,
  IconButton,
  FormControlLabel,
  Switch,
  TableSortLabel
} from '@mui/material';
import {
  Download as DownloadIcon,
  Login as LoginIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';

const LoginAnalytics = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('30'); // days or 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [teams, setTeams] = useState([]);
  const [teamGroups, setTeamGroups] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedTeamGroups, setSelectedTeamGroups] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [selectedFunder, setSelectedFunder] = useState('');
  const [funderSearch, setFunderSearch] = useState('');
  const [teamSearch, setTeamSearch] = useState('');
  const [teamGroupSearch, setTeamGroupSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [expandedUserId, setExpandedUserId] = useState(null);

  // ── Tab navigation ────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);

  // ── Team Engagement tab state ─────────────────────────────────────────────
  const [taStartDate, setTaStartDate] = useState('2026-02-01');
  const [taEndDate, setTaEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [taThreshold, setTaThreshold] = useState(2);
  const [taFunder, setTaFunder] = useState('');
  const [taFunderSearch, setTaFunderSearch] = useState('');
  const [taRunning, setTaRunning] = useState(false);
  const [taResults, setTaResults] = useState(null);
  const [taExcludeAdmins, setTaExcludeAdmins] = useState(true);
  const [taExpandedTeamId, setTaExpandedTeamId] = useState(null);
  const [taSort, setTaSort] = useState({ key: 'activeUsers', dir: 'desc' });
  const handleTaSort = (key) => setTaSort(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  const [selectedUser, setSelectedUser] = useState('');
  
  // Fetch all teams and team groups on component mount
  useEffect(() => {
    const fetchTeamsAndGroups = async () => {
      try {
        setLoadingTeams(true);
        // Get all teams
        const teamsResponse = await adminService.listTeams(0, 10000, enqueueSnackbar, true);
        const teamsData = teamsResponse.teams || [];
        setTeams(teamsData);
        
        // Get all team groups
        try {
          const groupsResponse = await adminService.listTeamGroups();
          console.log('Team groups response:', groupsResponse);
          const groupsData = groupsResponse.team_groups || groupsResponse.teamGroups || groupsResponse || [];
          setTeamGroups(groupsData);
          console.log('Loaded teams:', teamsData.length);
          console.log('Loaded team groups:', groupsData.length);
        } catch (groupError) {
          console.error('Error loading team groups:', groupError);
          // Don't fail if team groups can't be loaded
          setTeamGroups([]);
        }
      } catch (error) {
        console.error('Error fetching teams/groups:', error);
        enqueueSnackbar('Failed to load teams and team groups', { variant: 'error' });
      } finally {
        setLoadingTeams(false);
      }
    };
    
    fetchTeamsAndGroups();
  }, [enqueueSnackbar]);
  
  // Calculate date range based on selected time range
  const getDateRange = () => {
    // If custom range is selected, use the custom dates
    if (timeRange === 'custom') {
      if (!customStartDate || !customEndDate) {
        throw new Error('Please select both start and end dates');
      }
      return {
        start: new Date(customStartDate).toISOString(),
        end: new Date(customEndDate + 'T23:59:59').toISOString() // End of day
      };
    }
    
    // Otherwise use preset ranges
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - parseInt(timeRange));
    
    return {
      start: startDate.toISOString(),
      end: endDate.toISOString()
    };
  };
  
  // Fetch users for selected teams/groups
  const fetchTeamUsers = async () => {
    const hasTeamSelection = selectedTeams.length > 0;
    const hasGroupSelection = selectedTeamGroups.length > 0;
    const hasFunderSelection = selectedFunder !== '';
    
    if (!hasTeamSelection && !hasGroupSelection && !hasFunderSelection) {
      enqueueSnackbar('Please select at least one team, team group, or funder', { variant: 'warning' });
      return;
    }
    
    setLoading(true);
    try {
      // Get all users
      const usersResponse = await adminService.listUsers(0, 10000, enqueueSnackbar);
      const allUsers = usersResponse.users || [];
      
      console.log('=== DEBUG: User and Team Data ===');
      console.log('Total users fetched:', allUsers.length);
      console.log('Sample user structure:', allUsers[0]);
      console.log('Total teams available:', teams.length);
      console.log('Sample team structure:', teams[0]);
      
      // Helper function to get team IDs from user (handles both array and object formats)
      const getUserTeamIds = (user) => {
        if (!user.teams) return [];
        // If teams is an array of objects with 'id' property
        if (Array.isArray(user.teams)) {
          return user.teams.map(t => t.id).filter(Boolean);
        }
        // If teams is an object with team IDs as keys
        return Object.keys(user.teams);
      };
      
      // Helper function to get team group IDs from user
      const getUserTeamGroupIds = (user) => {
        if (!user.teamGroups) return [];
        // If teamGroups is an array of objects with 'id' property
        if (Array.isArray(user.teamGroups)) {
          return user.teamGroups.map(g => g.id).filter(Boolean);
        }
        // If teamGroups is an object with group IDs as keys
        return Object.keys(user.teamGroups);
      };
      
      // Check team ID formats
      if (allUsers[0]) {
        const userTeamIds = getUserTeamIds(allUsers[0]);
        console.log('Sample user team IDs:', userTeamIds);
        console.log('Sample system team IDs:', teams.slice(0, 3).map(t => t.id));
        console.log('User teams format:', Array.isArray(allUsers[0].teams) ? 'Array' : 'Object');
      }
      
      // Resolve funder team IDs if a funder is selected
      const funderTeamIds = selectedFunder
        ? teams.filter(t => t.funder === selectedFunder).map(t => t.id)
        : [];

      let filteredUsers = [];
      
      // If "All" teams or groups is selected (and no funder restriction), include all users
      if ((selectedTeams.includes('all') || selectedTeamGroups.includes('all')) && !selectedFunder) {
        filteredUsers = allUsers;
      } else {
        // Determine effective team IDs to filter by
        const effectiveTeamIds = selectedTeams.includes('all')
          ? [] // will be handled by inSelectedTeam below
          : selectedTeams.length > 0 
            ? [...new Set([...selectedTeams, ...funderTeamIds])]
            : funderTeamIds;

        filteredUsers = allUsers.filter(user => {
          const userTeamIds = getUserTeamIds(user);
          const userTeamGroupIds = getUserTeamGroupIds(user);
          
          // Funder filter: user must belong to at least one team in funder's teams
          if (selectedFunder && funderTeamIds.length > 0) {
            const inFunderTeam = funderTeamIds.some(teamId => userTeamIds.includes(teamId));
            if (!inFunderTeam) return false;
          }

          // If "All" was selected, pass everyone who already passed funder filter
          if (selectedTeams.includes('all') || selectedTeamGroups.includes('all')) return true;

          // Check if user belongs to any selected team
          const inSelectedTeam = effectiveTeamIds.length === 0 || 
            effectiveTeamIds.some(teamId => userTeamIds.includes(teamId));
          
          // Check if user belongs to any selected team group
          const inSelectedGroup = selectedTeamGroups.length === 0 || 
            selectedTeamGroups.some(groupId => userTeamGroupIds.includes(groupId));
          
          // If both teams and groups are selected, user must match at least one
          if (effectiveTeamIds.length > 0 && selectedTeamGroups.length > 0) {
            return inSelectedTeam || inSelectedGroup;
          }
          else if (effectiveTeamIds.length > 0) {
            return inSelectedTeam;
          }
          else if (selectedTeamGroups.length > 0) {
            return inSelectedGroup;
          }
          
          return false;
        });
      }
      
      console.log('Filtered users:', filteredUsers.length);
      console.log('Filtered user sample with teams:', filteredUsers.slice(0, 3).map(u => ({
        name: u.name,
        email: u.email,
        teams: u.teams,
        teamGroups: u.teamGroups
      })));
      
      setTeamUsers(filteredUsers);
      setSelectedUser(''); // reset user filter on new analysis
      
      // Fetch analytics for these users
      if (filteredUsers.length > 0) {
        const userIds = filteredUsers.map(u => u.uid);
        
        try {
          const { start, end } = getDateRange();
          
          console.log('Fetching analytics for', userIds.length, 'users');
          console.log('Date range:', { start, end });
          
          const analyticsData = await authService.getLoginAnalytics(
            userIds,
            start,
            end,
            enqueueSnackbar
          );
          
          setAnalytics(analyticsData);
          enqueueSnackbar(`Loaded analytics for ${filteredUsers.length} users`, { variant: 'success' });
        } catch (dateError) {
          enqueueSnackbar(dateError.message || 'Invalid date range', { variant: 'error' });
          return;
        }
      } else {
        setAnalytics(null);
        enqueueSnackbar('No users found for selected team(s)/group(s)', { variant: 'warning' });
      }
    } catch (error) {
      console.error('Error fetching team users:', error);
      enqueueSnackbar('Failed to load analytics', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle team selection change
  const handleTeamChange = (event) => {
    const value = event.target.value;
    
    // If "All" is selected, clear other selections
    if (value.includes('all') && !selectedTeams.includes('all')) {
      setSelectedTeams(['all']);
    } 
    // If other teams are selected after "All", remove "All"
    else if (value.includes('all') && value.length > 1) {
      setSelectedTeams(value.filter(v => v !== 'all'));
    }
    else {
      setSelectedTeams(value);
    }
  };
  
  // Handle team group selection change
  const handleTeamGroupChange = (event) => {
    const value = event.target.value;
    
    // If "All" is selected, clear other selections
    if (value.includes('all') && !selectedTeamGroups.includes('all')) {
      setSelectedTeamGroups(['all']);
    } 
    // If other groups are selected after "All", remove "All"
    else if (value.includes('all') && value.length > 1) {
      setSelectedTeamGroups(value.filter(v => v !== 'all'));
    }
    else {
      setSelectedTeamGroups(value);
    }
  };
  
  
  // Export data to CSV
  const exportToCSV = () => {
    if (!analytics || !analytics.analytics) {
      enqueueSnackbar('No data to export', { variant: 'warning' });
      return;
    }
    
    // Helper to get team IDs (handles both array and object formats)
    const getUserTeamIds = (user) => {
      if (!user?.teams) return [];
      if (Array.isArray(user.teams)) {
        return user.teams.map(t => t.id).filter(Boolean);
      }
      return Object.keys(user.teams);
    };
    
    // Helper to get team group IDs
    const getUserTeamGroupIds = (user) => {
      if (!user?.teamGroups) return [];
      if (Array.isArray(user.teamGroups)) {
        return user.teamGroups.map(g => g.id).filter(Boolean);
      }
      return Object.keys(user.teamGroups);
    };
    
    const headers = [
      'User ID', 
      'Name', 
      'Email', 
      'Teams',
      'Team Groups',
      timeRange === 'custom' 
        ? `Login Count (${customStartDate} to ${customEndDate})`
        : `Login Count (Last ${timeRange} Days)`, 
      'Total Login Count', 
      'Last Login'
    ];
    
    const rows = Object.entries(analytics.analytics).map(([userId, data]) => {
      const user = teamUsers.find(u => u.uid === userId);
      
      // Get team names
      const userTeamIds = getUserTeamIds(user);
      const userTeams = userTeamIds.map(teamId => {
        const team = teams.find(t => t.id === teamId);
        return team?.name || teamId;
      }).join('; ');
      
      // Get team group names
      const userGroupIds = getUserTeamGroupIds(user);
      const userTeamGroups = userGroupIds.map(groupId => {
        const group = teamGroups.find(g => g.id === groupId);
        return group?.name || groupId;
      }).join('; ');
      
      return [
        userId,
        data.name || '',
        data.email || '',
        userTeams,
        userTeamGroups,
        data.login_count,
        data.total_login_count,
        data.last_login_time || ''
      ];
    });
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    // Build filename based on selections
    let selectionName = '';
    if (selectedTeams.includes('all') || selectedTeamGroups.includes('all')) {
      selectionName = 'all';
    } else {
      const teamNames = selectedTeams.map(id => teams.find(t => t.id === id)?.name || id).join('_');
      const groupNames = selectedTeamGroups.map(id => teamGroups.find(g => g.id === id)?.name || id).join('_');
      selectionName = [teamNames, groupNames].filter(Boolean).join('_');
    }
    
    a.download = `login_analytics_${selectionName}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    enqueueSnackbar('Data exported successfully', { variant: 'success' });
  };
  
  // Calculate summary statistics
  const getSummaryStats = () => {
    if (!analytics || !analytics.analytics) return { totalUsers: 0, activeUsers: 0, totalLogins: 0, avgLogins: 0, maxLogins: 0, minLogins: 0 };
    
    const entries = Object.entries(analytics.analytics);
    const activeCounts = entries.filter(([_, data]) => data.login_count > 0).map(([_, data]) => data.login_count);
    const activeUsers = activeCounts.length;
    const totalLogins = activeCounts.reduce((sum, c) => sum + c, 0);
    const avgLogins = activeUsers > 0 ? (totalLogins / activeUsers).toFixed(1) : 0;
    const maxLogins = activeCounts.length > 0 ? Math.max(...activeCounts) : 0;
    const minLogins = activeCounts.length > 0 ? Math.min(...activeCounts) : 0;
    
    return {
      totalUsers: entries.length,
      activeUsers,
      totalLogins,
      avgLogins,
      maxLogins,
      minLogins
    };
  };

  // Compute per-funder-team stats for the rolled-up table
  const getFunderTeamStats = () => {
    if (!analytics || !analytics.analytics || !selectedFunder) return [];
    const funderTeams = teams.filter(t => t.funder === selectedFunder);
    
    return funderTeams.map(team => {
      const teamUserObjs = teamUsers.filter(u => {
        const ids = Array.isArray(u.teams) ? u.teams.map(t => t.id) : Object.keys(u.teams || {});
        return ids.includes(team.id);
      });
      const teamAnalytics = teamUserObjs
        .map(u => analytics.analytics[u.uid])
        .filter(Boolean);
      
      const activeCount = teamAnalytics.filter(a => a.login_count > 0).length;
      const totalLogins = teamAnalytics.reduce((s, a) => s + a.login_count, 0);
      const avgLogins = activeCount > 0 ? (totalLogins / activeCount).toFixed(1) : 0;
      const lastLogin = teamAnalytics
        .map(a => a.last_login_time)
        .filter(Boolean)
        .sort()
        .pop() || null;

      return {
        id: team.id,
        name: team.name,
        userCount: teamUserObjs.length,
        activeUsers: activeCount,
        totalLogins,
        avgLogins,
        lastLogin
      };
    }).sort((a, b) => b.totalLogins - a.totalLogins);
  };
  
  const stats = getSummaryStats();
  const availableFunders = [...new Set(teams.map(t => t.funder).filter(Boolean))].sort();
  const funderTeamStats = getFunderTeamStats();

  // Searchable subsets for the Select dropdowns — all use the same sticky-search pattern
  const fundersForSelector = useMemo(() => {
    if (!funderSearch.trim()) return availableFunders;
    const q = funderSearch.toLowerCase();
    return availableFunders.filter(f => f.toLowerCase().includes(q));
  }, [availableFunders, funderSearch]);

  const teamsForSelector = useMemo(() => {
    const base = selectedFunder ? teams.filter(t => t.funder === selectedFunder) : teams;
    if (!teamSearch.trim()) return base;
    const q = teamSearch.toLowerCase();
    return base.filter(t => t.name.toLowerCase().includes(q) || (t.funder || '').toLowerCase().includes(q));
  }, [teams, selectedFunder, teamSearch]);

  const groupsForSelector = useMemo(() => {
    if (!teamGroupSearch.trim()) return teamGroups;
    const q = teamGroupSearch.toLowerCase();
    return teamGroups.filter(g => g.name.toLowerCase().includes(q));
  }, [teamGroups, teamGroupSearch]);

  const usersForSelector = useMemo(() => {
    if (!analytics || !analytics.analytics) return [];
    const all = Object.entries(analytics.analytics).map(([uid, data]) => ({
      uid,
      label: data.name ? `${data.name} — ${data.email || uid}` : (data.email || uid),
    })).sort((a, b) => a.label.localeCompare(b.label));
    if (!userSearch.trim()) return all;
    const q = userSearch.toLowerCase();
    return all.filter(u => u.label.toLowerCase().includes(q));
  }, [analytics, userSearch]);

  // Team Engagement — funders list for selector
  const taFunders = useMemo(() => {
    if (!taFunderSearch.trim()) return availableFunders;
    const q = taFunderSearch.toLowerCase();
    return availableFunders.filter(f => f.toLowerCase().includes(q));
  }, [availableFunders, taFunderSearch]);

  // Team Engagement — run analysis
  // Stores raw member data per team; admin-filter + threshold applied reactively in the UI
  const runTeamEngagementAnalysis = useCallback(async () => {
    if (!taStartDate || !taEndDate) {
      enqueueSnackbar('Please select a start and end date', { variant: 'warning' });
      return;
    }
    setTaRunning(true);
    setTaResults(null);
    setTaExpandedTeamId(null);
    try {
      const usersResponse = await adminService.listUsers(0, 10000, enqueueSnackbar);
      const allUsers = usersResponse.users || [];

      const getUserTeamIds = (user) => {
        if (!user.teams) return [];
        if (Array.isArray(user.teams)) return user.teams.map(t => t.id).filter(Boolean);
        return Object.keys(user.teams);
      };

      const userIds = allUsers.map(u => u.uid);
      const start = new Date(taStartDate).toISOString();
      const end = new Date(taEndDate + 'T23:59:59').toISOString();

      const analyticsData = await authService.getLoginAnalytics(userIds, start, end, enqueueSnackbar);
      const analyticsMap = analyticsData.analytics || {};

      // uid → user object lookup for quick access to name/email/role
      const userById = {};
      allUsers.forEach(u => { userById[u.uid] = u; });

      // Build team → member list (all members, with role + login count)
      const teamMemberMap = {};
      teams.forEach(t => { teamMemberMap[t.id] = []; });
      allUsers.forEach(user => {
        getUserTeamIds(user).forEach(tid => {
          if (teamMemberMap[tid]) {
            const a = analyticsMap[user.uid];
            teamMemberMap[tid].push({
              uid: user.uid,
              name: user.name || user.display_name || user.email || user.uid,
              email: user.email || '',
              role: user.role || '',
              loginCount: (a && a.login_count) || 0,
            });
          }
        });
      });

      const teamData = teams.map(t => ({
        id: t.id,
        name: t.name,
        funder: t.funder || 'No Funder',
        schoolCount: t.school_count || 0,
        // Store ALL members (including empty teams); admin exclusion is applied reactively in the render
        members: teamMemberMap[t.id] || [],
      }));

      setTaResults({ teamData });
      const nonAdminActiveCount = teamData.filter(t => {
        const nonAdmin = t.members.filter(m => m.role !== 'admin');
        return nonAdmin.some(m => m.loginCount >= taThreshold);
      }).length;
      enqueueSnackbar(
        `Analysis complete — ${nonAdminActiveCount} of ${teamData.length} teams meet the threshold (admins excluded)`,
        { variant: 'success' }
      );
    } catch (err) {
      console.error('Team engagement analysis error:', err);
      enqueueSnackbar('Analysis failed — check the console for details', { variant: 'error' });
    } finally {
      setTaRunning(false);
    }
  }, [teams, taStartDate, taEndDate, taThreshold, enqueueSnackbar]);

  // Team Engagement — export CSV
  const exportTeamEngagementCSV = useCallback(() => {
    if (!taResults) return;
    const rows = taResults.teamData
      .filter(t => !taFunder || t.funder === taFunder)
      .map(t => {
        const members = taExcludeAdmins ? t.members.filter(m => m.role !== 'admin') : t.members;
        const activeUsers = members.filter(m => m.loginCount > 0).length;
        const totalLogins = members.reduce((s, m) => s + m.loginCount, 0);
        const adminCount = t.members.length - members.length;
        return {
          Funder: t.funder,
          'Team Name': t.name,
          'Total Users': members.length,
          'Schools': t.schoolCount || 0,
          ...(taExcludeAdmins && adminCount > 0 ? { 'Admins Excluded': adminCount } : {}),
          'Active Users (Period)': activeUsers,
          'Total Logins (Period)': totalLogins,
          [`Meets Threshold (≥${taThreshold} logins/user)`]: members.some(m => m.loginCount >= taThreshold) ? 'Yes' : 'No',
        };
      });
    if (!rows.length) return;
    const csv = [Object.keys(rows[0]).join(','), ...rows.map(r => Object.values(r).map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team-engagement-${taStartDate}-to-${taEndDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [taResults, taFunder, taThreshold, taExcludeAdmins, taStartDate, taEndDate]);

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={0} sx={{ mb: 2, p: 3, bgcolor: 'background.default' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <LoginIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Login Analytics
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track user login activity across teams and time periods
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Tab bar */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ px: 2 }}
        >
          <Tab label="Login Analytics" />
          <Tab label="Team Engagement" />
        </Tabs>
      </Paper>

      {/* ───── TAB 0 : Login Analytics (existing) ───── */}
      <Box hidden={activeTab !== 0}>

      {/* Configuration Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select Teams/Groups and Time Range
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Funder — single-select with sticky search */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={loadingTeams}>
                <InputLabel shrink>Funder</InputLabel>
                <Select
                  value={selectedFunder}
                  onChange={(e) => {
                    setSelectedFunder(e.target.value);
                    setSelectedTeams([]);
                  }}
                  input={<OutlinedInput notched label="Funder" />}
                  displayEmpty
                  onClose={() => setFunderSearch('')}
                  MenuProps={{
                    autoFocus: false,
                    PaperProps: { style: { maxHeight: 440 } },
                  }}
                  renderValue={(v) => v || 'All Funders'}
                >
                  <ListSubheader sx={{ pt: 1, pb: 0.5, bgcolor: 'background.paper' }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search funders…"
                      value={funderSearch}
                      onChange={(e) => setFunderSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </ListSubheader>
                  {!funderSearch && <MenuItem value="">All Funders</MenuItem>}
                  {fundersForSelector.map(f => (
                    <MenuItem key={f} value={f}>{f}</MenuItem>
                  ))}
                  {fundersForSelector.length === 0 && (
                    <MenuItem disabled><ListItemText primary="No funders match" /></MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Teams — multi-select with sticky search */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={loadingTeams}>
                <InputLabel>Teams</InputLabel>
                <Select
                  multiple
                  value={selectedTeams}
                  onChange={handleTeamChange}
                  input={<OutlinedInput label="Teams" />}
                  renderValue={(selected) => {
                    if (selected.includes('all')) return 'All Teams';
                    if (selected.length === 0) return '';
                    return `${selected.length} team(s) selected`;
                  }}
                  onClose={() => setTeamSearch('')}
                  MenuProps={{
                    autoFocus: false,
                    PaperProps: { style: { maxHeight: 440 } },
                  }}
                >
                  <ListSubheader sx={{ pt: 1, pb: 0.5, bgcolor: 'background.paper' }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search teams…"
                      value={teamSearch}
                      onChange={(e) => setTeamSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </ListSubheader>
                  {!teamSearch && (
                    <MenuItem value="all">
                      <Checkbox checked={selectedTeams.includes('all')} />
                      <ListItemText primary="All Teams" />
                    </MenuItem>
                  )}
                  {teamsForSelector.map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      <Checkbox checked={selectedTeams.indexOf(team.id) > -1} />
                      <ListItemText
                        primary={team.name}
                        secondary={team.funder ? `${team.funder} · ${team.school_count || 0} schools` : `${team.school_count || 0} schools`}
                      />
                    </MenuItem>
                  ))}
                  {teamsForSelector.length === 0 && (
                    <MenuItem disabled><ListItemText primary="No teams match" /></MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Team Groups — multi-select with sticky search */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth disabled={loadingTeams}>
                <InputLabel>Team Groups</InputLabel>
                <Select
                  multiple
                  value={selectedTeamGroups}
                  onChange={handleTeamGroupChange}
                  input={<OutlinedInput label="Team Groups" />}
                  renderValue={(selected) => {
                    if (selected.includes('all')) return 'All Groups';
                    if (selected.length === 0) return '';
                    return `${selected.length} group(s)`;
                  }}
                  onClose={() => setTeamGroupSearch('')}
                  MenuProps={{
                    autoFocus: false,
                    PaperProps: { style: { maxHeight: 440 } },
                  }}
                >
                  <ListSubheader sx={{ pt: 1, pb: 0.5, bgcolor: 'background.paper' }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search groups…"
                      value={teamGroupSearch}
                      onChange={(e) => setTeamGroupSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </ListSubheader>
                  {!teamGroupSearch && (
                    <MenuItem value="all">
                      <Checkbox checked={selectedTeamGroups.includes('all')} />
                      <ListItemText primary="All Team Groups" />
                    </MenuItem>
                  )}
                  {groupsForSelector.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      <Checkbox checked={selectedTeamGroups.indexOf(group.id) > -1} />
                      <ListItemText
                        primary={group.name}
                        secondary={`${group.team_count || 0} teams`}
                      />
                    </MenuItem>
                  ))}
                  {groupsForSelector.length === 0 && (
                    <MenuItem disabled><ListItemText primary="No groups match" /></MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* User — single-select with sticky search, always visible */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth disabled={!analytics || usersForSelector.length === 0}>
                <InputLabel shrink>User</InputLabel>
                <Select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  input={<OutlinedInput notched label="User" />}
                  displayEmpty
                  onClose={() => setUserSearch('')}
                  MenuProps={{
                    autoFocus: false,
                    PaperProps: { style: { maxHeight: 440 } },
                  }}
                  renderValue={(v) => {
                    if (!analytics) return 'Run analysis first';
                    if (!v) return 'All Users';
                    return usersForSelector.find(u => u.uid === v)?.label || 'All Users';
                  }}
                >
                  <ListSubheader sx={{ pt: 1, pb: 0.5, bgcolor: 'background.paper' }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search users…"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" color="action" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </ListSubheader>
                  {!userSearch && <MenuItem value="">All Users</MenuItem>}
                  {usersForSelector.map((u) => (
                    <MenuItem key={u.uid} value={u.uid}>
                      <ListItemText primary={u.label} />
                    </MenuItem>
                  ))}
                  {usersForSelector.length === 0 && !analytics && (
                    <MenuItem disabled><ListItemText primary="Run analysis to see users" /></MenuItem>
                  )}
                  {usersForSelector.length === 0 && analytics && (
                    <MenuItem disabled><ListItemText primary="No users match" /></MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Analyze button */}
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={fetchTeamUsers}
                disabled={loading || (selectedTeams.length === 0 && selectedTeamGroups.length === 0 && selectedFunder === '')}
                startIcon={loading ? <CircularProgress size={20} /> : <PeopleIcon />}
                sx={{ height: '56px' }}
              >
                Analyze
              </Button>
            </Grid>
          </Grid>
          
          {/* Time Range Selector - Full Width Row */}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={timeRange}
                  label="Time Range"
                  onChange={(e) => setTimeRange(e.target.value)}
                  startAdornment={<CalendarIcon sx={{ mr: 1, color: 'action.active' }} />}
                >
                  <MenuItem value="1">Last 24 Hours</MenuItem>
                  <MenuItem value="7">Last 7 Days</MenuItem>
                  <MenuItem value="30">Last 30 Days</MenuItem>
                  <MenuItem value="90">Last 90 Days</MenuItem>
                  <MenuItem value="365">Last Year</MenuItem>
                  <MenuItem value="custom">Custom Date Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Custom Date Pickers - Show when Custom is selected */}
            {timeRange === 'custom' && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      max: customEndDate || new Date().toISOString().split('T')[0]
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      min: customStartDate,
                      max: new Date().toISOString().split('T')[0]
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
          
          {(selectedTeams.length > 0 || selectedTeamGroups.length > 0 || selectedFunder) && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Selected: {[
                  selectedFunder ? `Funder: ${selectedFunder}` : null,
                  selectedTeams.includes('all') ? 'All Teams' : selectedTeams.length > 0 ? `${selectedTeams.length} team(s)` : null,
                  selectedTeamGroups.includes('all') ? 'All Team Groups' : selectedTeamGroups.length > 0 ? `${selectedTeamGroups.length} group(s)` : null
                ].filter(Boolean).join(' + ')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      
      {/* Summary Statistics */}
      {analytics && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
            {[
              { label: 'Total Users', value: stats.totalUsers, color: 'text.primary', sub: null },
              { label: 'Active Users (Period)', value: stats.activeUsers, color: 'primary.main', sub: `${stats.totalUsers > 0 ? ((stats.activeUsers / stats.totalUsers) * 100).toFixed(0) : 0}% of total` },
              { label: 'Total Logins (Period)', value: stats.totalLogins, color: 'success.main', sub: null },
              { label: 'Avg Logins/User', value: stats.avgLogins, color: 'text.primary', sub: 'Active users only' },
              { label: 'Max Logins (Period)', value: stats.maxLogins, color: 'info.main', sub: 'Single user high' },
              { label: 'Min Logins (Period)', value: stats.minLogins, color: 'warning.main', sub: 'Active users only' },
            ].map(({ label, value, color, sub }) => (
              <Grid item xs={12} sm={6} md={2} key={label}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', boxSizing: 'border-box' }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {label}
                    </Typography>
                    <Typography variant="h3" color={color} sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                      {value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ minHeight: '1.2em' }}>
                      {sub || ''}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Funder Rolled-Up Teams Table */}
          {selectedFunder && funderTeamStats.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Teams under {selectedFunder}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Team Name</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}># Users</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Active Users</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Total Logins (Period)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>Avg Logins / Active User</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {funderTeamStats.map(row => (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.name}</TableCell>
                          <TableCell align="right">{row.userCount}</TableCell>
                          <TableCell align="right">
                            <Chip label={row.activeUsers} size="small" color={row.activeUsers > 0 ? 'primary' : 'default'} />
                          </TableCell>
                          <TableCell align="right">{row.totalLogins}</TableCell>
                          <TableCell align="right">{row.avgLogins}</TableCell>
                          <TableCell>
                            {row.lastLogin ? new Date(row.lastLogin).toLocaleDateString() : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
          
          {/* Detailed User Table */}
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">
                  Detailed User Login Data
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={exportToCSV}
                  size="small"
                >
                  Export CSV
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedUser
                  ? `Showing 1 user`
                  : `${Object.keys(analytics.analytics).length} users`
                }{selectedUser && usersForSelector.find(u => u.uid === selectedUser) ? ` — ${usersForSelector.find(u => u.uid === selectedUser).label}` : ''} • Time range: {
                  timeRange === 'custom' 
                    ? `${new Date(customStartDate).toLocaleDateString()} - ${new Date(customEndDate).toLocaleDateString()}`
                    : `${new Date(analytics.start_date).toLocaleDateString()} - ${new Date(analytics.end_date).toLocaleDateString()}`
                }
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 40, p: 0.5 }} />
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Teams</TableCell>
                      <TableCell>Team Groups</TableCell>
                      <TableCell align="right">Logins (Period)</TableCell>
                      <TableCell align="right">Total Logins</TableCell>
                      <TableCell>Last Login</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(analytics.analytics)
                      .filter(([userId]) => !selectedUser || userId === selectedUser)
                      .sort(([, a], [, b]) => b.login_count - a.login_count)
                      .map(([userId, data]) => {
                        const userObj = teamUsers.find(u => u.uid === userId);
                        const isExpanded = expandedUserId === userId;
                        
                        const getUserTeamIds = (user) => {
                          if (!user?.teams) return [];
                          if (Array.isArray(user.teams)) return user.teams.map(t => t.id).filter(Boolean);
                          return Object.keys(user.teams);
                        };
                        const getUserTeamGroupIds = (user) => {
                          if (!user?.teamGroups) return [];
                          if (Array.isArray(user.teamGroups)) return user.teamGroups.map(g => g.id).filter(Boolean);
                          return Object.keys(user.teamGroups);
                        };
                        
                        const userTeamIds = getUserTeamIds(userObj);
                        const userTeamsList = userTeamIds.map(teamId => teams.find(t => t.id === teamId)?.name || teamId);
                        const userGroupIds = getUserTeamGroupIds(userObj);
                        const userGroupsList = userGroupIds.map(groupId => teamGroups.find(g => g.id === groupId)?.name || groupId);

                        // Build daily login counts from login_dates for the timeline
                        const loginDates = data.login_dates || [];
                        const byDay = {};
                        loginDates.forEach(iso => {
                          const key = iso.slice(0, 10);
                          byDay[key] = (byDay[key] || 0) + 1;
                        });
                        const maxDayCount = Math.max(1, ...Object.values(byDay));
                        const timelineStart = new Date(analytics.start_date);
                        const timelineEnd = new Date(analytics.end_date);
                        const totalMs = timelineEnd - timelineStart || 1;
                        const svgW = 800; const svgH = 72;
                        const padH = 24; const axisY = 44;
                        const xFor = (dateKey) => {
                          const d = new Date(dateKey + 'T12:00:00Z');
                          return padH + Math.min(1, Math.max(0, (d - timelineStart) / totalMs)) * (svgW - 2 * padH);
                        };
                        const monthTicks = [];
                        const cur = new Date(timelineStart.getFullYear(), timelineStart.getMonth() + 1, 1);
                        while (cur <= timelineEnd) {
                          const x = padH + ((cur - timelineStart) / totalMs) * (svgW - 2 * padH);
                          if (x > padH + 10 && x < svgW - padH - 10) {
                            monthTicks.push({ x, label: cur.toLocaleDateString('en-US', { month: 'short' }) });
                          }
                          cur.setMonth(cur.getMonth() + 1);
                        }

                        return (
                          <React.Fragment key={userId}>
                            <TableRow
                              hover
                              sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}
                              onClick={() => setExpandedUserId(isExpanded ? null : userId)}
                            >
                              <TableCell sx={{ width: 40, p: 0.5 }}>
                                <IconButton size="small">
                                  {isExpanded ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
                                </IconButton>
                              </TableCell>
                              <TableCell>{data.name || 'N/A'}</TableCell>
                              <TableCell>{data.email || 'N/A'}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                  {userTeamsList.length > 0 ? (
                                    userTeamsList.slice(0, 3).map((name, idx) => (
                                      <Chip key={idx} label={name} size="small" variant="outlined" sx={{ mb: 0.5 }} />
                                    ))
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">None</Typography>
                                  )}
                                  {userTeamsList.length > 3 && (
                                    <Chip label={`+${userTeamsList.length - 3}`} size="small" variant="outlined" sx={{ mb: 0.5 }} />
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                  {userGroupsList.length > 0 ? (
                                    userGroupsList.slice(0, 2).map((name, idx) => (
                                      <Chip key={idx} label={name} size="small" variant="outlined" color="secondary" sx={{ mb: 0.5 }} />
                                    ))
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">None</Typography>
                                  )}
                                  {userGroupsList.length > 2 && (
                                    <Chip label={`+${userGroupsList.length - 2}`} size="small" variant="outlined" color="secondary" sx={{ mb: 0.5 }} />
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell align="right">
                                <Chip label={data.login_count} color={data.login_count > 0 ? 'primary' : 'default'} size="small" />
                              </TableCell>
                              <TableCell align="right">{data.total_login_count || 0}</TableCell>
                              <TableCell>
                                {data.last_login_time ? new Date(data.last_login_time).toLocaleString() : 'Never'}
                              </TableCell>
                            </TableRow>

                            {/* Timeline expansion row */}
                            <TableRow>
                              <TableCell colSpan={8} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', borderRadius: 1, my: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                      Login Timeline — {loginDates.length > 0 ? `${loginDates.length} event${loginDates.length > 1 ? 's' : ''} tracked` : 'No individual events recorded (pre-tracking logins count only)'}
                                    </Typography>
                                    {loginDates.length > 0 ? (
                                      <svg
                                        viewBox={`0 0 ${svgW} ${svgH}`}
                                        preserveAspectRatio="xMidYMid meet"
                                        style={{ width: '100%', height: 'auto', display: 'block' }}
                                      >
                                        {/* Axis */}
                                        <line x1={padH} y1={axisY} x2={svgW - padH} y2={axisY} stroke="#bbb" strokeWidth="1.5" />
                                        {/* Start label */}
                                        <text x={padH} y={axisY + 14} fontSize="10" fill="#999" textAnchor="start">
                                          {timelineStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </text>
                                        {/* End label */}
                                        <text x={svgW - padH} y={axisY + 14} fontSize="10" fill="#999" textAnchor="end">
                                          {timelineEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </text>
                                        {/* Month ticks */}
                                        {monthTicks.map((tick, i) => (
                                          <g key={i}>
                                            <line x1={tick.x} y1={axisY - 4} x2={tick.x} y2={axisY + 4} stroke="#ddd" strokeWidth="1" />
                                            <text x={tick.x} y={axisY + 14} fontSize="9" fill="#bbb" textAnchor="middle">{tick.label}</text>
                                          </g>
                                        ))}
                                        {/* Login dots */}
                                        {Object.entries(byDay).map(([dateKey, count]) => {
                                          const x = xFor(dateKey);
                                          const r = 4 + (maxDayCount > 1 ? ((count - 1) / (maxDayCount - 1)) * 8 : 0);
                                          const d = new Date(dateKey);
                                          const label = `${d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}: ${count} login${count > 1 ? 's' : ''}`;
                                          return (
                                            <Tooltip key={dateKey} title={label} arrow placement="top">
                                              <circle cx={x} cy={axisY} r={r} fill="#1976d2" opacity="0.75" style={{ cursor: 'default' }} />
                                            </Tooltip>
                                          );
                                        })}
                                      </svg>
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">
                                        Login count: {data.login_count}. Individual timestamps are only available for logins recorded after tracking was deployed.
                                      </Typography>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
      
      {/* Info Alert */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Login events are tracked from the time this feature was deployed.
          Historical login counts are preserved but individual event records won't exist for past logins.
          Select one or more teams and/or team groups (or "All") to view login analytics for their users.
        </Typography>
      </Alert>

      </Box> {/* end Tab 0 */}

      {/* ───── TAB 1 : Team Engagement ───── */}
      <Box hidden={activeTab !== 1}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Team Engagement Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Identify which teams have at least <strong>1 user with {taThreshold}+ logins</strong> during the selected period.
              Defaults to <strong>Feb 1, 2026 — today</strong>, matching the start of the current program cycle.
            </Typography>

            <Grid container spacing={2} alignItems="center">
              {/* Start date */}
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={taStartDate}
                  onChange={(e) => setTaStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: taEndDate }}
                />
              </Grid>

              {/* End date */}
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={taEndDate}
                  onChange={(e) => setTaEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: taStartDate, max: new Date().toISOString().split('T')[0] }}
                />
              </Grid>

              {/* Threshold */}
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Min Logins per User"
                  type="number"
                  value={taThreshold}
                  onChange={(e) => setTaThreshold(Math.max(1, parseInt(e.target.value) || 1))}
                  inputProps={{ min: 1, step: 1 }}
                  helperText="At least 1 user must hit this"
                />
              </Grid>

              {/* Funder filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth disabled={loadingTeams}>
                  <InputLabel shrink>Funder (optional)</InputLabel>
                  <Select
                    value={taFunder}
                    onChange={(e) => setTaFunder(e.target.value)}
                    input={<OutlinedInput notched label="Funder (optional)" />}
                    displayEmpty
                    onClose={() => setTaFunderSearch('')}
                    MenuProps={{ autoFocus: false, PaperProps: { style: { maxHeight: 440 } } }}
                    renderValue={(v) => v || 'All Funders'}
                  >
                    <ListSubheader sx={{ pt: 1, pb: 0.5, bgcolor: 'background.paper' }}>
                      <TextField
                        size="small" fullWidth placeholder="Search funders…"
                        value={taFunderSearch}
                        onChange={(e) => setTaFunderSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
                      />
                    </ListSubheader>
                    {!taFunderSearch && <MenuItem value="">All Funders</MenuItem>}
                    {taFunders.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              {/* Run button */}
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth variant="contained" size="large"
                  startIcon={taRunning ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                  onClick={runTeamEngagementAnalysis}
                  disabled={taRunning || loadingTeams}
                >
                  {taRunning ? 'Running…' : 'Run Analysis'}
                </Button>
              </Grid>

              {/* Exclude admins toggle — full row */}
              <Grid item xs={12}>
                <Divider sx={{ mb: 1 }} />
                <FormControlLabel
                  control={
                    <Switch
                      checked={taExcludeAdmins}
                      onChange={(e) => setTaExcludeAdmins(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">Exclude admin users from analysis</Typography>
                      {taResults && (
                        <Typography variant="caption" color="text.secondary">
                          ({taResults.teamData.flatMap(t => t.members).filter(m => m.role === 'admin').length} admin(s) in dataset)
                        </Typography>
                      )}
                    </Stack>
                  }
                />
                {taExcludeAdmins && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 6, display: 'block', mt: -0.5 }}>
                    Admin users are assigned to teams but may inflate engagement counts — toggle off to include them.
                  </Typography>
                )}
              </Grid>
            </Grid>

            {taRunning && <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />}
          </CardContent>
        </Card>

        {/* Results */}
        {taResults && (() => {
          // Apply admin filter reactively — no re-run needed
          const withStats = taResults.teamData.map(t => {
            const members = taExcludeAdmins ? t.members.filter(m => m.role !== 'admin') : t.members;
            const activeUsers = members.filter(m => m.loginCount > 0).length;
            const totalLogins = members.reduce((s, m) => s + m.loginCount, 0);
            const adminCount = t.members.length - members.length;
            const meetsThreshold = members.some(m => m.loginCount >= taThreshold);
            return { ...t, members, activeUsers, totalLogins, totalUsers: members.length, adminCount, meetsThreshold };
          });

          const filtered = withStats.filter(t => !taFunder || t.funder === taFunder);
          const metFiltered = filtered.filter(t => t.meetsThreshold).length;

          const byFunder = {};
          filtered.forEach(t => { if (!byFunder[t.funder]) byFunder[t.funder] = []; byFunder[t.funder].push(t); });
          const funderOrder = Object.keys(byFunder).sort((a, b) => {
            if (a === 'No Funder') return 1; if (b === 'No Funder') return -1; return a.localeCompare(b);
          });

          return (
            <>
              {/* Summary tiles */}
              <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
                {[
                  { label: 'Teams Analyzed', value: filtered.length, color: 'text.primary', sub: taFunder || 'All funders' },
                  { label: `Teams with ≥${taThreshold} logins/user`, value: metFiltered, color: 'success.main', sub: `${filtered.length > 0 ? ((metFiltered / filtered.length) * 100).toFixed(0) : 0}% of teams` },
                  { label: 'Below Threshold', value: filtered.length - metFiltered, color: 'warning.main', sub: 'Need more engagement' },
                  { label: 'Period', value: `${new Date(taStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(taEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`, color: 'text.primary', sub: taExcludeAdmins ? 'Admins excluded' : 'Admins included', isText: true },
                ].map(({ label, value, color, sub, isText }) => (
                  <Grid item xs={12} sm={6} md={3} key={label}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', boxSizing: 'border-box' }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
                        <Typography variant={isText ? 'h6' : 'h3'} color={color} sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>{value}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ minHeight: '1.2em' }}>{sub || ''}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Results table */}
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Box>
                      <Typography variant="h6">Team Breakdown by Funder</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click a team row to see which users logged in
                      </Typography>
                    </Box>
                    <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportTeamEngagementCSV}>
                      Export CSV
                    </Button>
                  </Stack>

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell sx={{ width: 40, p: 0.5 }} />
                          <TableCell sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={taSort.key === 'name'}
                              direction={taSort.key === 'name' ? taSort.dir : 'asc'}
                              onClick={() => handleTaSort('name')}
                            >Team Name</TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={taSort.key === 'totalUsers'}
                              direction={taSort.key === 'totalUsers' ? taSort.dir : 'desc'}
                              onClick={() => handleTaSort('totalUsers')}
                            >Users</TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={taSort.key === 'schoolCount'}
                              direction={taSort.key === 'schoolCount' ? taSort.dir : 'desc'}
                              onClick={() => handleTaSort('schoolCount')}
                            >Schools</TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={taSort.key === 'activeUsers'}
                              direction={taSort.key === 'activeUsers' ? taSort.dir : 'desc'}
                              onClick={() => handleTaSort('activeUsers')}
                            >Active Users</TableSortLabel>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            <TableSortLabel
                              active={taSort.key === 'totalLogins'}
                              direction={taSort.key === 'totalLogins' ? taSort.dir : 'desc'}
                              onClick={() => handleTaSort('totalLogins')}
                            >Total Logins</TableSortLabel>
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {funderOrder.map(funder => {
                          const funderTeamsSorted = [...byFunder[funder]].sort((a, b) => {
                            const av = taSort.key === 'name' ? a[taSort.key] : (a[taSort.key] ?? 0);
                            const bv = taSort.key === 'name' ? b[taSort.key] : (b[taSort.key] ?? 0);
                            if (taSort.key === 'name') return taSort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
                            return taSort.dir === 'asc' ? av - bv : bv - av;
                          });
                          const funderTeams = funderTeamsSorted;
                          const funderMet = funderTeams.filter(t => t.meetsThreshold).length;
                          return (
                            <React.Fragment key={funder}>
                              {/* Funder subheader */}
                              <TableRow>
                                <TableCell
                                  colSpan={7}
                                  sx={{ py: 0.75, fontWeight: 700, fontSize: '0.82rem', color: 'primary.dark', letterSpacing: 0.3, bgcolor: 'action.selected' }}
                                >
                                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <span>{funder}</span>
                                    <Typography variant="caption" color="text.secondary">
                                      {funderMet} / {funderTeams.length} teams meet threshold
                                    </Typography>
                                  </Stack>
                                </TableCell>
                              </TableRow>

                              {/* Team rows with expansion */}
                              {funderTeams.map(t => {
                                const isExpanded = taExpandedTeamId === t.id;
                                const activeMembers = t.members.filter(m => m.loginCount > 0).sort((a, b) => b.loginCount - a.loginCount);
                                const inactiveMembers = t.members.filter(m => m.loginCount === 0);
                                // Admins excluded from t.members already (filtered above), but keep count for info
                                return (
                                  <React.Fragment key={t.id}>
                                    {/* Determine amber: someone logged in but nobody hit the threshold yet */}
                                    {(() => {
                                      const hasAnyLogins = t.activeUsers > 0;
                                      const maxUserLogins = t.members.length > 0 ? Math.max(...t.members.map(m => m.loginCount)) : 0;
                                      const rowAmber = !t.meetsThreshold && hasAnyLogins;
                                      return (
                                        <TableRow
                                          hover
                                          sx={{
                                            cursor: 'pointer',
                                            bgcolor: t.meetsThreshold ? 'rgba(76,175,80,0.06)' : rowAmber ? 'rgba(255,152,0,0.06)' : undefined,
                                            '& > *': { borderBottom: isExpanded ? 'none' : undefined },
                                          }}
                                          onClick={() => setTaExpandedTeamId(isExpanded ? null : t.id)}
                                        >
                                          <TableCell sx={{ width: 40, p: 0.5 }}>
                                            <IconButton size="small">
                                              {isExpanded ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
                                            </IconButton>
                                          </TableCell>
                                          <TableCell sx={{ pl: 3 }}>{t.name}</TableCell>
                                          <TableCell align="right">
                                            {t.totalUsers}
                                            {t.adminCount > 0 && (
                                              <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                                                ({t.adminCount} admin excl.)
                                              </Typography>
                                            )}
                                          </TableCell>
                                          <TableCell align="right">{t.schoolCount}</TableCell>
                                          <TableCell align="right">
                                            <Chip
                                              label={t.activeUsers}
                                              size="small"
                                              color={t.meetsThreshold ? 'success' : rowAmber ? 'warning' : 'default'}
                                              variant={t.activeUsers === 0 ? 'outlined' : 'filled'}
                                            />
                                          </TableCell>
                                          <TableCell align="right">{t.totalLogins}</TableCell>
                                          <TableCell align="center">
                                            {t.meetsThreshold ? (
                                              <Tooltip title={`A user has ≥ ${taThreshold} logins — qualifies`}>
                                                <CheckCircleIcon color="success" fontSize="small" />
                                              </Tooltip>
                                            ) : (
                                              <Tooltip title={!hasAnyLogins ? 'No logins recorded' : `Top user has ${maxUserLogins} login${maxUserLogins !== 1 ? 's' : ''} — needs ${taThreshold - maxUserLogins} more to qualify`}>
                                                <CancelIcon color={!hasAnyLogins ? 'disabled' : 'warning'} fontSize="small" />
                                              </Tooltip>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })()}

                                    {/* Expanded: user list */}
                                    <TableRow>
                                      <TableCell colSpan={7} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                          <Box sx={{ mx: 3, my: 1.5, bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
                                            {activeMembers.length > 0 ? (
                                              <>
                                                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75, color: 'success.dark' }}>
                                                  Logged In ({activeMembers.length})
                                                </Typography>
                                                <Table size="small" sx={{ mb: inactiveMembers.length > 0 ? 1.5 : 0 }}>
                                                  <TableHead>
                                                    <TableRow>
                                                      <TableCell sx={{ py: 0.5, fontWeight: 600 }}>Name</TableCell>
                                                      <TableCell sx={{ py: 0.5, fontWeight: 600 }}>Email</TableCell>
                                                      <TableCell align="right" sx={{ py: 0.5, fontWeight: 600 }}>Logins (Period)</TableCell>
                                                    </TableRow>
                                                  </TableHead>
                                                  <TableBody>
                                                    {activeMembers.map(m => (
                                                      <TableRow key={m.uid} sx={{ bgcolor: 'background.paper' }}>
                                                        <TableCell sx={{ py: 0.5 }}>{m.name}</TableCell>
                                                        <TableCell sx={{ py: 0.5 }}>{m.email}</TableCell>
                                                        <TableCell align="right" sx={{ py: 0.5 }}>
                                                          <Chip label={m.loginCount} size="small" color="success" variant="filled" />
                                                        </TableCell>
                                                      </TableRow>
                                                    ))}
                                                  </TableBody>
                                                </Table>
                                              </>
                                            ) : (
                                              <Typography variant="caption" color="text.secondary">
                                                {t.members.length === 0
                                                  ? 'No users assigned to this team.'
                                                  : 'No users logged in during this period.'}
                                              </Typography>
                                            )}

                                            {inactiveMembers.length > 0 && (
                                              <>
                                                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75, color: 'text.secondary' }}>
                                                  No Logins ({inactiveMembers.length})
                                                </Typography>
                                                <Table size="small">
                                                  <TableBody>
                                                    {inactiveMembers.map(m => (
                                                      <TableRow key={m.uid} sx={{ bgcolor: 'background.paper' }}>
                                                        <TableCell sx={{ py: 0.5 }}>{m.name}</TableCell>
                                                        <TableCell sx={{ py: 0.5 }}>{m.email}</TableCell>
                                                        <TableCell align="right" sx={{ py: 0.5 }}>
                                                          <Chip label="0" size="small" variant="outlined" color="default" />
                                                        </TableCell>
                                                      </TableRow>
                                                    ))}
                                                  </TableBody>
                                                </Table>
                                              </>
                                            )}
                                          </Box>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  </React.Fragment>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </>
          );
        })()}

        {!taResults && !taRunning && (
          <Alert severity="info">
            Configure the date range and threshold above, then click <strong>Run Analysis</strong> to see which teams have sufficient engagement.
          </Alert>
        )}
      </Box> {/* end Tab 1 */}

    </Box>
  );
};

export default LoginAnalytics;
